import type { ModelConfig } from './ModelConfig';
import type { Peripheral, PeripheralManager } from '../emulator/peripheral/PeripheralManager';
import type { InterruptController } from '../emulator/interrupt/InterruptController';
import { ConfigError } from '../types';

import { MMU } from '../emulator/mmu/MMU';
import { InterruptController as InterruptControllerImpl } from '../emulator/interrupt/InterruptController';
import { PeripheralManager as PeripheralManagerImpl } from '../emulator/peripheral/PeripheralManager';
import { CPU } from '../emulator/cpu/CPU';
import { Chipset } from '../emulator/chipset/Chipset';

export interface EmulatorComponents {
  chipset: Chipset;
  cpu: CPU;
  mmu: MMU;
  interruptController: InterruptController;
  peripheralManager: PeripheralManager;
}

export interface PeripheralOptions {
  interruptController: InterruptController;
  modelConfig: ModelConfig;
  storage?: import('../storage/StorageProvider').StorageProvider;
}

const PERIPHERAL_CONSTRUCTORS: Record<string, new (options: PeripheralOptions) => Peripheral> = {};

export function registerPeripheralType(type: string, ctor: new (options: PeripheralOptions) => Peripheral): void {
  PERIPHERAL_CONSTRUCTORS[type] = ctor;
}

export function createPeripheral(type: string, options: PeripheralOptions): Peripheral {
  const Ctor = PERIPHERAL_CONSTRUCTORS[type];
  if (!Ctor) {
    throw new ConfigError(`Unknown peripheral type: ${type}`);
  }
  return new Ctor(options);
}

function createUnmappedRegion() {
  return {
    name: 'UNMAPPED',
    read8: () => 0xFF,
    write8: () => {},
  };
}

function createROMRegion(romData: Uint8Array) {
  return {
    name: 'ROM',
    read8: (address: number) => romData[address & 0xFFFF] ?? 0xFF,
    write8: (_address: number, _value: number) => {},
  };
}

export function createEmulatorFromModel(config: ModelConfig, storage?: import('../storage/StorageProvider').StorageProvider): EmulatorComponents {
  const mmu = new MMU();
  const interruptController = new InterruptControllerImpl();
  const peripheralManager = new PeripheralManagerImpl();

  for (const seg of config.segments) {
    switch (seg.type) {
      case 'ROM':
        if (config.romData) {
          mmu.mapSegment(seg.segment, createROMRegion(config.romData));
        }
        break;
      case 'RAM':
        mmu.mapRAM(seg.segment, seg.size ?? 0x10000);
        break;
      case 'UNMAPPED':
        mmu.mapSegment(seg.segment, createUnmappedRegion());
        break;
      case 'MIRROR':
        break;
      case 'PERIPHERAL':
        break;
    }
  }

  for (const def of config.peripherals) {
    if (!def.enabled) continue;
    try {
      const peripheral = createPeripheral(def.type, {
        interruptController,
        modelConfig: config,
        storage,
      });
      peripheralManager.register(peripheral);
    } catch {
      // peripheral type not yet registered; skip
    }
  }

  peripheralManager.initAll(mmu, config);

  const cpu = new CPU({ mmu, interruptController });

  const chipset = new Chipset({
    cpu,
    mmu,
    interruptController,
    peripheralManager,
    modelConfig: config,
  });

  return { chipset, cpu, mmu, interruptController, peripheralManager };
}
